import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NotebookPen, Pencil } from 'lucide-react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { cn } from '../../lib/utils'
import type { WeeklyCheckIn, WeekSummary } from '../../types/weekly-check-in'

const MAX_BODY_LENGTH = 2000

interface WeeklyCheckInCardProps {
  checkIn: WeeklyCheckIn | null
  summary?: WeekSummary
  /** Opens the editor on mount — the reminder links here to be answered. */
  autoOpen?: boolean
  onSave: (body: string) => Promise<void>
}

/**
 * The week's own account of itself: one paragraph, written by the athlete.
 *
 * It asks a question rather than a battery of them. Sleep and joint pain scored
 * out of ten would be health data (Art. 9), which this product does not hold —
 * see the migration that creates the table. The sessions counter beside it is
 * computed from the diary, not asked: the numbers are the part we already know.
 */
export function WeeklyCheckInCard({ checkIn, summary, autoOpen = false, onSave }: WeeklyCheckInCardProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(!checkIn)
  const [body, setBody] = useState(checkIn?.body ?? '')
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Follow the week the page is on: switching weeks reuses this component.
  useEffect(() => {
    setBody(checkIn?.body ?? '')
    setEditing(!checkIn)
  }, [checkIn])

  useEffect(() => {
    if (!autoOpen) return
    setEditing(true)
    textareaRef.current?.focus()
  }, [autoOpen])

  async function handleSave() {
    const trimmed = body.trim()
    if (!trimmed) return

    setSaving(true)
    try {
      await onSave(trimmed)
      setEditing(false)
    } catch {
      // The page says what went wrong; what was typed stays here to try again.
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setBody(checkIn?.body ?? '')
    setEditing(false)
  }

  const sessionsLabel = summary
    ? summary.sessions_planned != null
      ? t('trainingDiary.weeklyCheckIn.sessionsWithPlan', '{{done}}/{{planned}} sessions', {
          done: summary.sessions_done,
          planned: summary.sessions_planned,
        })
      : t('trainingDiary.weeklyCheckIn.sessions', '{{done}} sessions', {
          done: summary.sessions_done,
        })
    : null

  return (
    <div id="weekly-check-in" className="rounded-xl border p-3 flex flex-col gap-2 scroll-mt-32">
      <div className="flex items-center gap-2">
        <NotebookPen className="size-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="text-sm font-medium">
          {t('trainingDiary.weeklyCheckIn.title', 'This week')}
        </span>
        {sessionsLabel && (
          <span className="ml-auto text-xs text-muted-foreground shrink-0">{sessionsLabel}</span>
        )}
      </div>

      {editing ? (
        <>
          <Textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={MAX_BODY_LENGTH}
            rows={4}
            className="text-sm resize-none"
            placeholder={t(
              'trainingDiary.weeklyCheckIn.placeholder',
              'Tell us how you rested this week, the sessions you did…'
            )}
          />
          <div className="flex items-center justify-end gap-2">
            {checkIn && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleCancel}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs"
              onClick={handleSave}
              disabled={saving || !body.trim()}
            >
              {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2">
          <p className={cn('text-sm whitespace-pre-wrap flex-1 min-w-0')}>{checkIn?.body}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs shrink-0"
            onClick={() => setEditing(true)}
            aria-label={t('common.edit', 'Edit')}
          >
            <Pencil className="size-3.5" aria-hidden />
          </Button>
        </div>
      )}
    </div>
  )
}
